import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Subscription } from 'rxjs'

import {
  BackgroundJob,
  JobNotification,
  BackgroundJobMonitorService
} from '../../services/background-job-monitor.service'
import { environment } from '../../../environments/environment'

@Component({
  selector: 'sbpro-job-status',
  templateUrl: './job-status.component.html',
  styleUrls: ['./job-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobStatusComponent implements OnInit, OnDestroy {
  activeJobs: BackgroundJob[] = []
  notifications: JobNotification[] = []
  completedAndFailedNotifications: JobNotification[] = []
  isMonitoring = false
  showJobDetails = false
  showNotifications = false

  private readonly MAX_COMPLETED_NOTIFICATIONS = 25
  private readonly subscriptions = new Subscription()

  constructor (
    private readonly jobMonitorService: BackgroundJobMonitorService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly http: HttpClient
  ) {}

  ngOnInit(): void {
    const userID = localStorage.getItem('userID')
    const adminID = localStorage.getItem('adminID')
    const isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')

    if (userID && !isAdminRoute) {
      this.refreshJobsFromServer()
    }

    if (adminID && isAdminRoute) {
      this.refreshAdminJobsFromServer()
    }

    this.subscriptions.add(
      this.jobMonitorService.activeJobs$.subscribe(jobsMap => {
        const jobs = Array.from(jobsMap.values())
        this.activeJobs = this.filterActiveJobs(jobs)
        this.changeDetectorRef.markForCheck()
      })
    )

    this.subscriptions.add(
      this.jobMonitorService.notifications$.subscribe(notifications => {
        this.notifications = notifications
        this.completedAndFailedNotifications = notifications
          .filter(notification => {
            const displayStatus = this.getNotificationDisplayStatus(notification)
            return displayStatus === 'completed' || displayStatus === 'failed'
          })
          .slice(0, this.MAX_COMPLETED_NOTIFICATIONS)
        this.changeDetectorRef.markForCheck()
      })
    )

    this.subscriptions.add(
      this.jobMonitorService.isMonitoring$.subscribe(isMonitoring => {
        this.isMonitoring = isMonitoring
        this.changeDetectorRef.markForCheck()
      })
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
  }

  toggleJobDetails(): void {
    this.showJobDetails = !this.showJobDetails
    if (this.showJobDetails) {
      this.showNotifications = false
    }
    this.changeDetectorRef.markForCheck()
  }

  toggleNotifications(): void {
    this.showNotifications = !this.showNotifications
    if (this.showNotifications) {
      this.showJobDetails = false
    }
    this.changeDetectorRef.markForCheck()
  }

  clearCompletedJobs(): void {
    this.jobMonitorService.clearCompletedJobs()
  }

  clearAllNotifications(): void {
    this.jobMonitorService.clearNotifications()
  }

  removeNotification(jobId: string): void {
    this.jobMonitorService.removeNotification(jobId)
  }

  keepNotification(jobId: string): void {
    console.log(`Keeping notification for job: ${jobId}`)
  }

  downloadFile(url: string, filename: string): void {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  downloadCompletedFile(jobId: string, apiScope: 'client' | 'admin' = 'client'): void {
    const downloadPath = apiScope === 'admin' ? 'admin/common/download_report' : 'common/download_report'
    const adminID = localStorage.getItem('adminID')
    const params =
      apiScope === 'admin' && adminID
        ? new HttpParams().set('adminID', adminID)
        : undefined
    this.http
      .get(`${environment.apiUrl}${downloadPath}/${jobId}`, {
        params,
        responseType: 'blob',
        observe: 'response'
      })
      .subscribe(res => {
        const blob = res.body as Blob
        let filename = 'downloaded-file'
        const contentDisposition = res.headers.get('Content-Disposition')
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition)
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/["']/g, '')
          }
        }

        const url = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = filename
        anchor.click()
        window.URL.revokeObjectURL(url)
      })
  }

  getJobTypeIcon(type: string): string {
    switch (type) {
      case 'export':
        return 'fas fa-file-export'
      case 'bulk-sync':
        return 'fas fa-sync-alt'
      case 'batch-invoice':
        return 'fas fa-receipt'
      default:
        return 'fas fa-cog'
    }
  }

  getJobTypeLabel(type: string): string {
    switch (type) {
      case 'export':
        return 'Data Export'
      case 'bulk-sync':
        return 'Bulk Sync'
      case 'batch-invoice':
        return 'Batch Invoice'
      default:
        return type.charAt(0).toUpperCase() + type.slice(1)
    }
  }

  getNotificationTitle(notification: JobNotification): string {
    return notification.name || this.getJobTypeLabel(notification.type || 'export')
  }

  getNotificationMessage(notification: JobNotification): string {
    const normalizedStatus = this.getNotificationDisplayStatus(notification)

    if (normalizedStatus === 'failed') {
      return notification.error || notification.message || 'This job failed.'
    }

    if (normalizedStatus === 'completed') {
      return notification.message || 'This file is ready for download.'
    }

    return notification.message || ''
  }

  getNotificationStatus(notification: JobNotification): string {
    return this.getNotificationDisplayStatus(notification)
  }

  isCompletedNotification(notification: JobNotification): boolean {
    return this.getNotificationDisplayStatus(notification) === 'completed'
  }

  isFailedNotification(notification: JobNotification): boolean {
    return this.getNotificationDisplayStatus(notification) === 'failed'
  }

  canDownloadNotification(notification: JobNotification): boolean {
    return this.getNotificationDisplayStatus(notification) === 'completed' && !!notification.jobId
  }

  hasNotificationExpiry(notification: JobNotification): boolean {
    return this.canDownloadNotification(notification) && !!notification.expiresAt
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'pending':
        return 'fas fa-clock'
      case 'processing':
        return 'fas fa-spinner fa-spin'
      case 'completed':
        return 'fas fa-check'
      case 'failed':
        return 'fas fa-exclamation-triangle'
      default:
        return 'fas fa-question'
    }
  }

  getStatusClass(status: string): string {
    return `status-${status}`
  }

  getProgressGradient(progress: number): string {
    if (progress < 30) {
      return 'linear-gradient(90deg, #dc3545, #fd7e14)'
    } else if (progress < 70) {
      return 'linear-gradient(90deg, #fd7e14, #ffc107)'
    } else if (progress < 100) {
      return 'linear-gradient(90deg, #ffc107, #28a745)'
    }
    return 'linear-gradient(90deg, #28a745, #20c997)'
  }

  formatDuration(startTime: string | Date): string {
    const start = startTime instanceof Date ? startTime : this.parseServerDate(startTime)
    const diff = Date.now() - start.getTime()
    if (diff < 0) return 'In future'

    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) return `${days}d ${hours % 24}h ago`
    if (hours > 0) return `${hours}h ${minutes % 60}m ago`
    if (minutes > 0) return `${minutes}m ago`
    return 'Just started'
  }

  getStatusIconTuple(status: string): any {
    switch (this.normalizeNotificationStatus(status)) {
      case 'pending':
        return ['fas', 'clock']
      case 'processing':
        return ['fas', 'spinner']
      case 'completed':
        return ['fas', 'check']
      case 'failed':
        return ['fas', 'exclamation-triangle']
      default:
        return ['fas', 'question']
    }
  }

  getJobTypeIconTuple(type: string): any {
    switch (type) {
      case 'export':
        return ['fas', 'file-export']
      case 'bulk-sync':
        return ['fas', 'arrows-rotate']
      case 'batch-invoice':
        return ['fas', 'receipt']
      default:
        return ['fas', 'cog']
    }
  }

  isSpinning(status: string): boolean {
    return this.normalizeNotificationStatus(status) === 'processing'
  }

  trackByJobId (_: number, job: BackgroundJob): string {
    return job.contextKey || job.id
  }

  trackByNotificationId (_: number, notification: JobNotification): string {
    return notification.contextKey || notification.jobId
  }

  private filterActiveJobs(jobs: BackgroundJob[]): BackgroundJob[] {
    return jobs.filter(job => !this.isJobCompleted(job) && job.status?.toLowerCase() !== 'failed')
  }

  private isJobCompleted(job: BackgroundJob): boolean {
    const status = job.status?.toLowerCase()
    if (status === 'completed') {
      return true
    }

    if (typeof job.progress === 'number') {
      return job.progress >= 100
    }

    return false
  }

  private refreshJobsFromServer(): void {
    this.subscriptions.add(
      this.jobMonitorService.refreshJobsFromServer().subscribe({
        next: () => this.changeDetectorRef.markForCheck(),
        error: error => console.error('Failed to refresh background jobs', error)
      })
    )
  }

  private refreshAdminJobsFromServer(): void {
    const adminID = localStorage.getItem('adminID')
    if (!adminID) {
      return
    }

    this.subscriptions.add(
      this.jobMonitorService.refreshJobsFromServer(adminID, 'admin').subscribe({
        next: () => this.changeDetectorRef.markForCheck(),
        error: error => console.error('Failed to refresh admin background jobs', error)
      })
    )
  }

  public parseServerDate(value: string | Date | null | undefined): Date {
    if (!value) {
      return new Date(NaN)
    }

    if (value instanceof Date) {
      return value
    }

    if (value.includes('T')) {
      return new Date(value)
    }

    const match = value.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})[ T]([0-9]{2}):([0-9]{2}):([0-9]{2})$/)
    if (match) {
      const [, year, month, day, hour, minute, second] = match.map(Number)
      const utcMs = Date.UTC(year, month - 1, day, hour - 2, minute, second)
      return new Date(utcMs)
    }

    return new Date(value)
  }

  private normalizeNotificationStatus(status: string | null | undefined): string {
    const normalized = String(status || '').trim().toLowerCase()

    switch (normalized) {
      case 'complete':
      case 'completed':
      case 'done':
      case 'success':
      case 'succeeded':
        return 'completed'
      case 'failed':
      case 'error':
      case 'errored':
        return 'failed'
      case 'pending':
      case 'queued':
        return 'pending'
      case 'processing':
      case 'running':
      case 'in-progress':
      case 'in_progress':
        return 'processing'
      default:
        return normalized || 'processing'
    }
  }

  private getNotificationDisplayStatus(notification: JobNotification): string {
    const normalized = this.normalizeNotificationStatus(notification.status)
    const hasFailureState = normalized === 'failed' || !!notification.error

    if (hasFailureState) {
      return 'failed'
    }

    if (normalized === 'pending' || normalized === 'processing') {
      return normalized
    }

    const hasDownloadMetadata = !!(notification.downloadUrl || notification.expiresAt)
    if (normalized === 'completed' || hasDownloadMetadata) {
      return 'completed'
    }

    return normalized
  }
}
